import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { InputOTP } from '@components/InputOTP';
import { Logo } from '@components/Logo';
import type { SSOProvider } from '@components/SSOButton';
import { SSOButton } from '@components/SSOButton';
import { IconLoading, IconMail } from '@components/icons';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import styles from './LoginPage.module.scss';

export type LoginPageState = 'email' | 'otp' | 'verifying';

export interface LoginPageProps {
  /** Custom gradient CSS value (defaults to Figma design gradient) */
  backgroundGradient?: string;

  /** SSO providers to display (defaults to all) */
  ssoProviders?: SSOProvider[];

  /** OTP length (4-10, default 6) */
  otpLength?: number;

  /** Show email form (set to false for SSO-only mode) */
  showEmailForm?: boolean;

  /** Current state of the login page (controlled mode) */
  initialState?: LoginPageState;

  /** Show verifying spinner (set externally when OTP is being validated) */
  isVerifying?: boolean;

  /** Show authenticating spinner after SSO sign-in, before redirect */
  isAuthenticating?: boolean;

  /** Called when user submits email */
  onEmailSubmit?: (email: string) => void;

  /** Called when SSO button is clicked */
  onSSOClick?: (provider: SSOProvider) => void;

  /** Called when OTP is complete */
  onOTPComplete?: (otp: string) => void;

  /** Additional CSS classes */
  className?: string;
}

// Default gradient from Figma design (node-id=232-1615)
// Pink/purple to blue at 123 degrees
const DEFAULT_GRADIENT =
  'linear-gradient(123deg, rgba(199, 81, 192, 1) 0%, rgba(65, 88, 208, 1) 100%)';

export const LoginPage = ({
  backgroundGradient = DEFAULT_GRADIENT,
  ssoProviders: ssoProvidersProp = ['google', 'microsoft', 'github'],
  otpLength = 6,
  showEmailForm: showEmailFormProp = true,
  initialState: initialStateProp = 'email',
  isVerifying: isVerifyingProp = false,
  isAuthenticating = false,
  onEmailSubmit,
  onSSOClick,
  onOTPComplete,
  className = '',
}: LoginPageProps) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  // Derive state from URL query params (for demo purposes)
  const { initialState, isVerifying, showEmailForm, ssoProviders, demoEmail } = useMemo(() => {
    const stateParam = searchParams.get('state');
    const modeParam = searchParams.get('mode');

    // Handle mode=google-sso (Google SSO only)
    if (modeParam === 'google-sso') {
      return {
        initialState: 'email' as LoginPageState,
        isVerifying: isVerifyingProp,
        showEmailForm: false,
        ssoProviders: ['google'] as SSOProvider[],
        demoEmail: '',
      };
    }

    // Handle state=otp (OTP entry) - include demo email for preview
    if (stateParam === 'otp') {
      return {
        initialState: 'otp' as LoginPageState,
        isVerifying: false,
        showEmailForm: showEmailFormProp,
        ssoProviders: ssoProvidersProp,
        demoEmail: 'user@example.com',
      };
    }

    // Handle state=verifying (OTP verifying spinner) - include demo email for preview
    if (stateParam === 'verifying') {
      return {
        initialState: 'otp' as LoginPageState,
        isVerifying: true,
        showEmailForm: showEmailFormProp,
        ssoProviders: ssoProvidersProp,
        demoEmail: 'user@example.com',
      };
    }

    // Default: use props
    return {
      initialState: initialStateProp,
      isVerifying: isVerifyingProp,
      showEmailForm: showEmailFormProp,
      ssoProviders: ssoProvidersProp,
      demoEmail: '',
    };
  }, [searchParams, initialStateProp, isVerifyingProp, showEmailFormProp, ssoProvidersProp]);

  const [pageState, setPageState] = useState<LoginPageState>(initialState);
  const [email, setEmail] = useState(demoEmail);
  const [otp, setOtp] = useState('');

  const handleEmailSubmit = useCallback(() => {
    if (email.trim()) {
      onEmailSubmit?.(email);
      setPageState('otp');
    }
  }, [email, onEmailSubmit]);

  const handleEmailKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleEmailSubmit();
      }
    },
    [handleEmailSubmit]
  );

  const handleOTPChange = useCallback((value: string) => {
    setOtp(value);
  }, []);

  const handleOTPComplete = useCallback(
    (value: string) => {
      onOTPComplete?.(value);
    },
    [onOTPComplete]
  );

  const handleSSOClick = useCallback(
    (provider: SSOProvider) => {
      onSSOClick?.(provider);
    },
    [onSSOClick]
  );

  const containerClassName = [styles.container, className].filter(Boolean).join(' ');

  return (
    <div className={containerClassName} style={{ background: backgroundGradient }}>
      <div className={styles.card}>
        {pageState === 'email' ? (
          <>
            {/* Main Content */}
            <div className={styles.cardContent}>
              {/* Header */}
              <div className={styles.header}>
                <Logo variant="black" />
                <p className={styles.subtitle}>
                  {isAuthenticating
                    ? t('login.authenticating', 'Signing you in...')
                    : t(
                        'login.subtitle',
                        "We'll sign you in or create an account if you don't have one yet."
                      )}
                </p>
              </div>

              {/* Authenticating Spinner - shown after SSO sign-in, before redirect */}
              {isAuthenticating ? (
                <div className={styles.authenticatingSpinner}>
                  <IconLoading size={120} color="var(--color-primary-base)" />
                </div>
              ) : (
                <>
                  {/* Email Form - only show if showEmailForm is true */}
                  {showEmailForm && (
                    <div className={styles.form}>
                      <Input
                        type="email"
                        value={email}
                        onChange={setEmail}
                        placeholder={t('login.emailPlaceholder', 'Enter your email')}
                        onKeyDown={handleEmailKeyDown}
                        leadingIcon={<IconMail size={18} />}
                      />
                      <Button variant="primary" size="large" onClick={handleEmailSubmit}>
                        {t('login.continueWithEmail', 'Continue with email')}
                      </Button>
                    </div>
                  )}

                  {/* SSO Buttons */}
                  {ssoProviders.length > 0 && (
                    <div className={styles.ssoButtons}>
                      {ssoProviders.map((provider) => (
                        <SSOButton
                          key={provider}
                          provider={provider}
                          onClick={() => handleSSOClick(provider)}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer - positioned at bottom on mobile */}
            <div className={styles.footer}>
              <p className={styles.footerText}>
                {t('login.agreementPrefix', 'We will process your data as set forth in our')}{' '}
                <a href="/data-processing" className={styles.footerLink}>
                  {t('login.dataProcessingAgreement', 'Data Processing Agreement')}
                </a>
              </p>
            </div>
          </>
        ) : (
          <>
            {/* OTP Content */}
            <div className={styles.cardContent}>
              {/* OTP Header */}
              <div className={styles.header}>
                <Logo variant="black" />
                <p className={styles.subtitle}>
                  {t('login.otpSubtitle', 'We have sent a one-time password to your email')}{' '}
                  <strong>{email || 'your email'}</strong>.
                </p>
              </div>

              {/* OTP Input Section */}
              <div className={styles.otpSection}>
                <p className={styles.otpInstruction}>
                  {t('login.otpInstruction', 'Please paste (or type) your 6-digit code:')}
                </p>

                {/* OTP Input or Verifying Spinner */}
                <div className={styles.otpForm}>
                  {isVerifying ? (
                    <div className={styles.verifyingSpinner}>
                      <div className={styles.spinner} />
                      <p className={styles.verifyingText}>
                        {t('login.verifying', 'Checking code...')}
                      </p>
                    </div>
                  ) : (
                    <InputOTP
                      value={otp}
                      onChange={handleOTPChange}
                      onComplete={handleOTPComplete}
                      length={otpLength}
                      // eslint-disable-next-line jsx-a11y/no-autofocus -- autoFocus improves UX for OTP entry
                      autoFocus
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Footer - same as email screen */}
            <div className={styles.footer}>
              <p className={styles.footerText}>
                {t('login.agreementPrefix', 'We will process your data as set forth in our')}{' '}
                <a href="/data-processing" className={styles.footerLink}>
                  {t('login.dataProcessingAgreement', 'Data Processing Agreement')}
                </a>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
